import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup, NG_VALUE_ACCESSOR, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { InternalTransferActions } from '../../../create-request/actions';
import { isRestrictedToAccountEntered, selectIsBundledOneTimeTotalDist, selectIsFromEdit, selectMultipleItAccountInfoBanner, selectTransactionType, selectTransferType } from '../../../create-request/reducers';
import { currencyStringToNumber } from '../../../create-request/utilities/currencyStringToNumber';
import { createControl } from '../../../create-request/utilities/formUtilities';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { ValidateInputService } from '../../services/validate-input.service';
import { amountFormatter } from '../../utilities/AmountFormatter';
import { addMaxAmountError, amountErrorMap, Constants, overdraftRulesError, overdraftRulesErrorTypes, transactionTypes } from '../../utilities/Constants';
import { amountAllowedChars, amountAllowedPattern, whitespaceNewlinesAndTabs } from '../../utilities/regex-patterns';
import { AchTransactionDetailsForm, CheckTransactionDetailsForm, ItTransactionDetailsForm, WireTransactionDetailsForm } from '../../../create-request/models/form/TransactionFormGroup';
import { selectHasPositionLotsPhase2 } from '../../../authorize/reducers';
import { FeatureFlagService } from '../../services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-amount-field',
  templateUrl: './amount-field.component.html',
  styleUrls: ['./amount-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AmountFieldComponent),
    }
  ]
})
export class AmountFieldComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm | WireTransactionDetailsForm | ItTransactionDetailsForm | AchTransactionDetailsForm>;
  @Input() maxTransferAmount: string | null;
  @Input() autoCalculateRmd$: Observable<boolean> = of(false);
  @Input() payIncome$: Observable<string | undefined> = of(undefined);;
  @Input() frequency$: Observable<"recurring" | "onetime" | "onetimeTotalDist" | undefined> = of("onetime");
  @Input() label: string | null;
  @Output() showWiresHighDollarAmountWarning = new EventEmitter<boolean>();
  labelId: string = 'amount-label';
  amountAllowedChars = amountAllowedChars;
  transferType: string;
  transactionType: string;
  wireHighDollarAmountText = Constants.wireHighDollarAmountWarningText;
  destroy$: Subject<boolean> = new Subject<boolean>();
  positionLotsPhase2EnabledFsg: boolean = false;
  validators: ValidatorFn[] = [
    Validators.required,
    CustomValidators.greaterThanZero,
    CustomValidators.onlyOneDecimal,
    CustomValidators.isANumber,
    CustomValidators.only2DecimalPlaces,
    CustomValidators.lessThan14Digits,
  ];
  isOneTimeTotalDist: boolean;

  constructor(private store: Store, public validationService: ValidateInputService, private featureFlagService: FeatureFlagService) {
    this.store.dispatch(
      InternalTransferActions.transactionDetailsComponentLoaded()
    );
  }

  ngOnInit(): void {
    if (!!this.label) {
      this.labelId = 'it-multiple-input-label';
    } else {
      this.labelId = 'amount-label';
    }
    if (this.maxTransferAmount) {
      this.validators.push(maxTransferAmountValidator(this.maxTransferAmount));
    }

    createControl(this.parentForm, 'amount', '', this.validators);
    
    this.store.select(selectTransactionType).pipe(takeUntil(this.destroy$)).subscribe(type => {
      this.transactionType = type;
    });

    combineLatest([
      this.autoCalculateRmd$.pipe(distinctUntilChanged()),
      this.payIncome$,
      this.frequency$.pipe(take(1)),
      this.store.select(selectMultipleItAccountInfoBanner).pipe(take(1)),
      this.store.select(isRestrictedToAccountEntered),
      this.store.select(selectIsBundledOneTimeTotalDist)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([autoCalculateRmd, payIncome, selectedExtraPromptsFrequency, accountInfoBanner, isRestrictedToAccountEntered, isBundledOneTimeTotalDist]) => {
        this.isOneTimeTotalDist = selectedExtraPromptsFrequency === 'onetimeTotalDist' || isBundledOneTimeTotalDist;
        this.amount?.enable();
        if (payIncome) {
          this.amount?.setValue('');
          this.amount?.disable();
        } else if (autoCalculateRmd) {
          this.amount?.setValue(Constants.zeroAmount);
          this.amount?.disable();
          this.amount?.markAsUntouched();
        } else if (this.isOneTimeTotalDist) {
          if (this.transactionType === 'check'){
            this.amount?.disable();
          }
          else if (this.transactionType === 'internal-transfer') {
            if (isRestrictedToAccountEntered) {
              this.amount?.disable();
            } else {
              this.amount?.setValue(accountInfoBanner.availableCashMMFAmount);
              this.amount?.disable();
            }
          }
        }
      });

    this.store.select(selectHasPositionLotsPhase2).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.positionLotsPhase2EnabledFsg = val;
    });

    this.store.select(selectTransferType).pipe(takeUntil(this.destroy$)).subscribe(type => {
      this.transferType = (typeof type === 'string') ? type : "";
    });

    if (this.transferType === "positionsCash" && this.positionLotsPhase2Enabled()){
      this.removePositionsCashValidations();
    }

    this.store.select(selectIsFromEdit).pipe(takeUntil(this.destroy$)).subscribe((isFromEdit) => {
      if(isFromEdit){
          if (this.transactionType === 'check' && this.isOneTimeTotalDist){
            this.amount?.setValue('');
            this.amount?.disable();
          }
      }
    });
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl('amount');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  clearAmount(): void {
    this.parentForm.controls.amount?.reset();
    this.parentForm.controls.amount?.markAsTouched();
  }

  positionLotsPhase2Enabled(): boolean {
    return this.featureFlagService.featureFlags.positionLotsPhase2Enabled || this.positionLotsPhase2EnabledFsg;
  }

  removePositionsCashValidations(){
    const requiredIndex = this.validators.indexOf(Validators.required);
    this.validators.splice(requiredIndex, 1);
    const greaterThanZeroIndex = this.validators.indexOf(CustomValidators.greaterThanZero);
    this.validators.splice(greaterThanZeroIndex, 1);
    if (this.amount) {
      this.amount.setValidators(this.validators);
      this.amount.updateValueAndValidity();
    }
  }

  get amountFieldLabel() {
    if (!!this.label){
      return this.label;
    } else {
      return this.transferType === 'positionsCash' ? 'Cash' : 'Amount';
    }
  }

  get amount() {
    return this.parentForm.controls.amount;
  }

  get amountType() {
    return this.parentForm.controls.amountType;
  }

  get amountError() {
    if (this.amount && this.amount.errors) {
      const errorKeys: string[] = Object.keys(this.amount.errors ?? {});
      if (errorKeys.length > 0) {
        if (errorKeys.includes('exceedsMaxTransferAmount')) {
          addMaxAmountError(this.maxTransferAmount!);
        }
        return amountErrorMap[errorKeys[0] as keyof typeof amountErrorMap];
      }
    }
  }

  get insufficientFundErrorPtrs(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(this.parentForm.errors ?? {});
      return errorKeys.length > 0 ? overdraftRulesError[errorKeys[0] as overdraftRulesErrorTypes] : '';
    }
    return '';
  }

  get hasInsufficientFundError(): boolean {
    if (this.parentForm && this.parentForm.errors) {
      let errorKey: overdraftRulesErrorTypes = 'insufficientFundError';
      return this.parentForm.errors[errorKey] ?? false;
    }
    return false;
  }

  get amountFieldIsInvalid(): boolean {
    return (this.parentForm.controls.amount?.invalid && this.parentForm.controls.amount.touched) || this.hasInsufficientFundError;
  }

  get highDollarAmountWarning(): boolean | undefined {
    if (this.transactionType === transactionTypes.wire && currencyStringToNumber(this.amount?.value ?? '') >= Constants.wireHighDollarAmount) {
      this.showWiresHighDollarAmountWarning.emit(true);
      return true;
    }
    this.showWiresHighDollarAmountWarning.emit(false);
  }

  formatAmount(): void {
    if (this.amount && this.amount.valid) {
      let temp = Number(this.amount?.value.replace(/[^0-9.]/g, '')); // replace everything besides numbers and decimals
      this.parentForm.controls.amount?.setValue(amountFormatter.format(temp));
    }
  }

  getAmountFieldContainerClass(): string{
    if (!!this.label) {
      return 'amount-field-container';
    }
    return '';
  }

  onPaste(event: ClipboardEvent) {
    //block non-numeric inputs
    if (event.clipboardData != undefined) {
      let pastedInput = event.clipboardData.getData('text/plain');
      pastedInput = pastedInput.replace(whitespaceNewlinesAndTabs, '');
      if (!amountAllowedPattern.test(pastedInput)) {
        event.preventDefault();
      }
    }
  }
}

export function maxTransferAmountValidator(maxAmount: string): ValidatorFn {
  let maxAmountConverted = currencyStringToNumber(maxAmount)
  return (control: AbstractControl) => {
    if (currencyStringToNumber(control.value) > maxAmountConverted) {
      return { 'exceedsMaxTransferAmount': true }
    }
    return null;
  }
}
